import { useMemo, useRef, useState } from 'react';

import { ActionIcon, ButtonMantine, FileButton } from '../../../Buttons';
import { Card, ImageMantine } from '../../../DataDisplay';
import { IconTrash, UploadIcon } from '../../../Icons';
import { Group } from '../../../Layout';
import { Text } from '../../../Typography';
import classes from './FileUpload.module.css';
import { ImageCropperModal } from './ImageCropper';

export interface FileUploadProps {
  file?: File | null;
  setFile: (file: File | null) => void;
  existingSrc?: {
    url?: string;
    filename?: string;
  };
  setExistingSrc?: () => void;
  enableCropping?: boolean;
  dimensions?: [number, number];
  croppedImageFileName?: string | ((croppedImageFile: File) => string);
  disabled?: boolean;
  accepts?: string;
}

/**
 * `FileUpload` is a React component for uploading files, specifically images.
 * It provides a UI for selecting a file, displaying a preview of the selected file,
 * and removing the selected file.
 *
 * The component can accept an existing source (URL and filename), but any new file
 * selected by the user will take priority over the existing source.
 */
export const FileUpload = ({
  accepts = 'image/png,image/jpeg',
  file,
  setFile,
  existingSrc,
  setExistingSrc,
  enableCropping = false,
  dimensions,
  croppedImageFileName,
  disabled,
}: FileUploadProps) => {
  const resetRef = useRef<() => void>(null);
  const [preCroppedFile, setPreCroppedFile] = useState<File>();
  const [source, setSource] = useState<undefined | FileUploadProps['existingSrc']>(existingSrc);
  const isSrcOrFile = !!source || !!file;

  const imageFileName = useMemo(() => {
    if (!croppedImageFileName || !preCroppedFile) {
      return undefined;
    }

    if (typeof croppedImageFileName === 'function') {
      return croppedImageFileName(preCroppedFile);
    }

    return croppedImageFileName;
  }, [croppedImageFileName, preCroppedFile]);

  const clearFile = () => {
    resetRef.current?.();
  };

  return (
    <Card bg="gray.0" radius={'sm'} className={classes.card} p="xs">
      <Group justify="space-between">
        <Group gap="xs">
          {isSrcOrFile ? (
            <ImageMantine
              src={file ? URL.createObjectURL(file) : source?.url}
              h={48}
              w={48}
              radius="xs"
            />
          ) : (
            <UploadIcon />
          )}
          <Text fz={10} c="gray.8">
            {isSrcOrFile ? (file ? file.name : '') : 'Upload an image'}
          </Text>
        </Group>
        {isSrcOrFile && !disabled ? (
          <ActionIcon
            variant="subtle"
            color="cerise.7"
            onClick={() => {
              if (source) {
                setSource(undefined);
                setExistingSrc?.();
              } else {
                setFile(null);
              }
              clearFile();
            }}
          >
            <IconTrash />
          </ActionIcon>
        ) : (
          <FileButton
            resetRef={resetRef}
            onChange={(file) => {
              // Clicking on a file triggers the image crop modal.

              if (file) {
                if (enableCropping) {
                  setPreCroppedFile(file);
                } else {
                  setFile(file);
                }
              }
            }}
            accept={accepts}
            disabled={disabled}
          >
            {(props) => (
              // I left this as small .. the design does not match any buttons we have, closest is 'xs'
              // design has 4px radius and 12px font; not in design library
              <ButtonMantine size="sm" mr="xs" disabled={disabled} {...props}>
                Browse Files
              </ButtonMantine>
            )}
          </FileButton>
        )}
      </Group>
      <ImageCropperModal
        opened={enableCropping && !!preCroppedFile}
        dimensions={dimensions}
        onCropped={(croppedImage) => {
          setFile(croppedImage);
          // Closes the modal
          setPreCroppedFile(undefined);
        }}
        imageUrl={preCroppedFile ? URL.createObjectURL(preCroppedFile) : undefined}
        croppedImageFileName={imageFileName}
        onClose={() => {
          clearFile();
          setPreCroppedFile(undefined);
        }}
      />
    </Card>
  );
};
